import type { components } from '@connected/api-client/api.generated';

import type { ObservationLog, ObservationLogEntryStatus } from './observation-log-types';

import { DISTRIBUTION_BOARDS_OBSERVATION_KINDS, FINAL_CIRCUITS_OBSERVATION_KINDS } from './observation-log-constants';

export function getLogEntryStatus(status: ObservationLogEntryStatus) {
    switch (status) {
        case 'IssueReported':
            return {
                label: 'Probleem gemeld',
            };
        case 'IssueSolved':
            return {
                label: 'Gereed gemeld',
            };
        case 'SolutionApproved':
            return {
                label: 'Oplossing goedgekeurd',
            };
        case 'SolutionRejected':
            return {
                label: 'Oplossing afgekeurd',
            };
        case 'ToSolveIssue':
            return {
                label: 'Gepland',
            };
        case 'WontSolveIssue':
            return {
                label: 'Wordt niet opgelost',
            };
        default:
            return {
                label: 'Onbekende status',
            };
    }
}

/**
 * Type predicate to check if an ObjectKind is a distribution board observation kind.
 * This provides type narrowing for TypeScript.
 */
function isDistributionBoardObservationKind(
    objectKind: components['schemas']['ElectricalProjectObservationObjectKind'],
) {
    return (DISTRIBUTION_BOARDS_OBSERVATION_KINDS as readonly string[]).includes(objectKind);
}

function isFinalCircuitObservationKind(
    objectKind: components['schemas']['ElectricalProjectObservationObjectKind'],
) {
    return (FINAL_CIRCUITS_OBSERVATION_KINDS as readonly string[]).includes(objectKind);
}

type ObservationLogIdentifierKind
    = components['schemas']['ElectricalProjectDistributionBoardDeviceKind']
        | components['schemas']['ElectricalProjectObservationObjectKind']
        | components['schemas']['ElectricalCheckListItemKind'];

type ObservationLogIdentifier = {
    id: string;
    identifier: string;
    kind?: ObservationLogIdentifierKind;
};

export function getObservationLogIdentifier(observationLog: ObservationLog): ObservationLogIdentifier | null {
    if (isDistributionBoardObservationKind(observationLog.ObjectKind!)) {
        return {
            id: observationLog.ElectricalProjectDistributionBoard!.ElectricalProjectDistributionBoardId!,
            identifier: observationLog.ElectricalProjectDistributionBoard!.Identifier!,
            kind: observationLog.ObjectKind!,
        };
    }
    else if (isFinalCircuitObservationKind(observationLog.ObjectKind!)) {
        return {
            id: observationLog.ElectricalProjectFinalCircuit!.ElectricalProjectFinalCircuitId!,
            identifier: observationLog.ElectricalProjectFinalCircuit!.Identifier!,
            kind: observationLog.ObjectKind!,
        };
    }
    else if (observationLog.ObjectKind === 'CheckListItem') {
        return {
            id: observationLog.ElectricalProjectCheckListItem!.ElectricalProjectCheckListItemId!,
            identifier: observationLog.ElectricalProjectCheckListItem!.Label!,
            kind: observationLog.ElectricalProjectCheckListItem!.Kind!,
        };
    }
    else if (observationLog.ObjectKind === 'RisoMeasurement') {
        return {
            id: observationLog.ElectricalProjectRisoMeasurement!.ElectricalProjectRisoMeasurementId!,
            identifier: observationLog.ElectricalProjectRisoMeasurement!.CircuitKind!,
            kind: observationLog.ObjectKind!,
        };
    }
    else if (observationLog.ObjectKind === 'DistributionBoardDevice') {
        return {
            id: observationLog.ElectricalProjectDistributionBoardDevice!.ElectricalProjectDistributionBoardDeviceId!,
            identifier: observationLog.ElectricalProjectDistributionBoardDevice!.Identifier!,
            kind: observationLog.ElectricalProjectDistributionBoardDevice!.DeviceKind!,
        };
    }
    else if (observationLog.ObjectKind === 'PVInverter') {
        return {
            id: observationLog.ElectricalProjectPVInverter!.ElectricalProjectPVInverterId!,
            identifier: observationLog.ElectricalProjectPVInverter!.Identifier!,
            kind: observationLog.ObjectKind!,
        };
    }
    return null;
}
